/**
 * Keep html2pdf from slicing a final page of zero height.
 *
 * html2pdf renders the WHOLE report into one canvas and then cuts it into
 * page-sized strips (node_modules/html2pdf.js/src/worker.js, toPdf). The last
 * strip is sized `pxFullHeight % pxPageHeight` — which is ZERO when the content
 * happens to end exactly on a page boundary. `toDataURL` on a zero-height canvas
 * returns the string "data:," and jsPDF answers that with
 *
 *   Supplied Data is not a valid base64-String jsPDF.convertStringToImageData
 *
 * so the whole export dies, and the last page of content is lost with it.
 *
 * An exact boundary is not the freak event it sounds like. html2pdf's own
 * pagebreak plugin pads every keep-together element out to the next boundary
 * (plugin/pagebreaks.js:115-130); the comparison marks every table row, every
 * section and the disclaimer as keep-together, and the analysis report is
 * pre-paginated onto the page grid by hand. Both are pushed onto that grid all
 * the way down, so a layout change lands the total on the line sooner or later —
 * which is how this arrives, as a report that exported yesterday and does not
 * today.
 *
 * Trimming one pixel off the BOTTOM moves the remainder from 0 to
 * pxPageHeight - 1: the same pages, the last one a pixel short of full, in the
 * margin where there is nothing to lose. Adding a pixel instead would be a whole
 * extra page, blank.
 *
 * Reaches into `worker.prop` because html2pdf exposes no other handle on the
 * canvas between the two halves of a run. `prop` lives on the chain's root
 * object and every link inherits it, so writing through it is what the library's
 * own steps do.
 *
 * Call it between `await worker.toCanvas()` and `worker.toPdf()`. Returns a
 * one-line description of the capture, for the failure message if the rest of
 * the run throws anyway — a practitioner can screenshot that, and it says more
 * than any guess.
 */
export function avoidEmptyFinalSlice(worker: any, label = 'PDF'): string {
  const canvas = worker?.prop?.canvas;
  const ratio = worker?.prop?.pageSize?.inner?.ratio;
  if (!canvas || !ratio) {
    const why = `no ${!canvas ? 'canvas' : 'page size'} after the capture`;
    console.error(`[${label}] ${why}`);
    return why;
  }

  const pxPageHeight = Math.floor(canvas.width * ratio);
  // A canvas with no height at all is a failed capture, not a boundary landing:
  // trimming it would ask for a height of -1. Chrome hands back an unusable
  // canvas when it is asked for one over 65,535px on a side, which is what a
  // report of more than ~60 pages comes to at this slice height.
  if (pxPageHeight <= 0 || canvas.height <= 0) {
    const why = `nothing captured: canvas ${canvas.width}x${canvas.height}`;
    console.error(`[${label}] ${why}`);
    return why;
  }

  const remainder = canvas.height % pxPageHeight;
  const summary =
    `canvas ${canvas.width}x${canvas.height}, slice ${pxPageHeight}px, ` +
    `${Math.ceil(canvas.height / pxPageHeight)} pages, ` +
    `final ${remainder || pxPageHeight}px`;
  console.log(`[${label}] ${summary}`);
  if (remainder !== 0) return summary;

  const trimmed = document.createElement('canvas');
  trimmed.width = canvas.width;
  trimmed.height = canvas.height - 1;
  const ctx = trimmed.getContext('2d');
  if (!ctx) return `${summary} (no 2d context to trim with)`;
  ctx.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    trimmed.height,
    0,
    0,
    canvas.width,
    trimmed.height,
  );
  worker.prop.canvas = trimmed;
  console.log(
    `[${label}] content ended exactly on a page boundary; trimmed 1px so the ` +
      'last page is not sliced to zero height',
  );
  return `${summary}, trimmed to ${trimmed.height}`;
}
